import { h, Fragment } from 'preact'
import { useState, useMemo, useEffect, useRef } from 'preact/hooks'
import MonogramInput from './monogram-input'

export default function FreeGiftSelector({ products, checkoutButton, ...props }) {
  const [selectedId, setSelectedId] = useState(props.selectedId)
  const monogramInputRef = useRef()

  useEffect(() => {
    checkoutButton.addEventListener('click', onCheckout, true)
    return () => checkoutButton.removeEventListener('click', onCheckout, true)
  }, [checkoutButton])

  const selectedProduct = useMemo(
    () => products.find((product) => product.variant_id === selectedId),
    [selectedId, products]
  )

  const availableProducts = useMemo(
    () => products.filter(product => product.variant_id),
    [products]
  )

  useEffect(() => {
    if (selectedProduct && selectedProduct.monogrammable) {
      monogramInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [selectedProduct])

  const onCheckout = (e) => {
    // The values in this class aren't accessible from the form event, so we have to pull them manually from the DOM
    const selectedItem = document.querySelector('.free-item-option[data-selected]')
    if (!selectedItem) return

    e.preventDefault()
    e.stopPropagation()

    // Validate monogram (if monogram form is present)
    const monogramForm = document.querySelector('form.free-item-monogram')
    if (monogramForm) {
      // I can't figure out how to use the built-in validation for the monogram-input component since
      // we're technically calling this from the DOM and not Preact, so I'm using a bit of cut-and-paste here, sorry!
      const formElements = monogramForm.elements
      const noMonogram = formElements.no_monogram.checked
      const initials = ['monogram_first', 'monogram_middle', 'monogram_last'].map((id) => formElements[id].value)
      const error = !noMonogram && initials.every((val) => /^[^A-Z]?$/.test(val))

      if (error) {
        const msg = monogramForm.querySelector('.fill-out-monogram-message')
        msg.classList.add('display')
        monogramForm.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }
    }

    const checkoutButton = e.currentTarget
    checkoutButton.classList.add('loading')
    cartUpdate(selectedItem).then((result) => {
      if (!result) {
        checkoutButton.classList.remove('loading')
      }
    })
  }

  const cartUpdate = async (selectedItem) => {
    const selectedVariantId = parseInt(selectedItem.dataset.variantId, 10)

    const updates = {}
    // First set the quantity to 0 for all items
    document.querySelectorAll('.free-item-option').forEach(option => {
      updates[option.dataset.variantId] = 0
    })
    // Then set the quantity to 1 for the selected item.
    updates[selectedVariantId] = 1

    let response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    })
    const json = await response.json()
    if (json.status && json.status >= 400) {
      if (json.description) {
        window.alert(json.description)
      }
      return false
    }

    // Find the line item for the newly added item
    const lineItem = json.items.find(lineItem => lineItem.variant_id === selectedVariantId)
    if (lineItem === undefined) {
      window.alert('Free gift did not save properly. Please try again.')
      return false
    }

    // Update the monogram for the item
    const monogramForm = document.querySelector('form.free-item-monogram')
    if (monogramForm) {
      const monogramFormData = new FormData(monogramForm)
      const data = {
        id: lineItem.key,
        properties: Object.fromEntries(monogramFormData)
      }
      response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })
      if (response.status === 404) {
        window.alert('Error when updating monogram for free gift. Monogram not saved.')
        return false
      }
    }

    checkoutButton.removeEventListener('click', onCheckout, true)
    checkoutButton.click()
    return true
  }

  const onGiftSelect = (e) => {
    setSelectedId(parseInt(e.currentTarget.dataset.variantId, 10))
  }

  const FreeGiftOption = ({ product }) => {
    return (
      <div class="free-item-option" onClick={onGiftSelect} data-variant-id={product.variant_id} data-selected={product.variant_id === selectedId && 'true'}>
        <img src={product.image_url} alt={product.title} />
        {product.variant_id === selectedId &&
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" class="check-circle" viewBox="0 0 18 18">
            <circle cx="9" cy="9" r="9" fill="#ccc" />
            <circle cx="9" cy="9" r="8" />
            <path d="m 11.798169,6.0128656 a 0.235,0.235 0 0 0 -0.02,0.022 l -3.4730003,4.4250004 -2.0929999,-2.094 a 0.75,0.75 0 0 0 -1.06,1.06 l 2.6459999,2.646999 a 0.75,0.75 0 0 0 1.079,-0.02 L 12.869169,7.0628656 a 0.75,0.75 0 0 0 -1.071,-1.05 z" fill="#fff" />
          </svg>
        }
      </div>
    )
  }

  if (availableProducts.length > 0) {
    return (
      <>
        <h4>Your Purchase Qualifies for a Free Gift! Select one:</h4>
        <div class="free-item-option-wrapper">
          {availableProducts.map(product => <FreeGiftOption product={product} />)}
        </div>
        {selectedProduct &&
          <div class="free-item-details" ref={monogramInputRef}>
            <h2>{selectedProduct.title}</h2>
            {selectedProduct.monogrammable &&
              <form class="free-item-monogram">
                <MonogramInput
                monogramStraight={selectedProduct.monogrammable === 'straight'}
                prependInputNames={false}
                {...props} />
                <div class="fill-out-monogram-message">
                  Please enter a monogram with at least one letter.<br />
                  If you do not want a monogram, check <span class="important-no-wrap">No Monogram</span> above.
                </div>
              </form>
            }
          </div>
        }
      </>
    )
  }
}
